import { useState } from "react";
import { History } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Loader } from "@/components/shared/loader";
import { useUserActivity } from "@/features/users/hooks/useUsersQueries";
import type { User } from "@/types/user";
import { formatOptionalDateTime, formatArabicDateTime } from "@/lib/formatters";

interface UserActivitySheetProps {
  user: User;
}

export function UserActivitySheet({ user }: UserActivitySheetProps) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useUserActivity(user.id, { page: 1, limit: 20 }, open);

  const logs = data?.items ?? [];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center text-gray-600 hover:text-indigo-600 transition-colors"
          aria-label="سجل النشاط"
          title="سجل النشاط"
        >
          <History className="w-4 h-4" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="text-right">
          <SheetTitle>سجل نشاط المستخدم</SheetTitle>
          <SheetDescription className="text-right">
            آخر دخول: {formatOptionalDateTime(user.lastLoginAt)}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          )}
          {error && (
            <p className="text-sm text-red-600 text-right">
              تعذر تحميل سجل النشاط.
            </p>
          )}
          {!isLoading && !error && logs.length === 0 && (
            <p className="text-sm text-gray-500 text-right">لا يوجد نشاط مسجّل.</p>
          )}
          {logs.map((log) => (
            <div
              key={log.id}
              className="rounded-lg border border-gray-200 p-3 space-y-2 text-right"
            >
              <div className="flex flex-wrap gap-2 justify-end">
                <Badge variant="outline">{log.action}</Badge>
                <Badge variant="secondary">{log.resource}</Badge>
              </div>
              <p className="text-xs text-gray-500">
                {formatArabicDateTime(new Date(log.createdAt))}
              </p>
              {log.ipAddress && (
                <p className="text-xs text-gray-400">IP: {log.ipAddress}</p>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
